// @ts-nocheck
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box, Tabs, Tab, Typography, Icon, Tooltip } from '@material-ui/core';
import { EmptyState, Panel, TabPanel } from '../../../common/components';
import HouseholdService from '../services/HouseholdService';
import HouseholdLogs from './partials/HouseholdLogs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import HouseholdDevices from './partials/HouseholdDevices';
import { Action, Household, HouseholdLog, HouseholdLogsResponse } from '../types';
import { useAlert } from '../../../common/hooks';
import { AlertSeverity } from '../../../common/providers';


const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    minHeight: '500px',
  },
  content: {
    flex: '1',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  title: {
    '& *': {
      fontSize: theme.typography.h5.fontSize,
    },
  },
}));

const TabIndex = {
  Household: 0,
  Logs: 1,
};

function HouseholdPage() {
  const classes = useStyles();
  const { userId } = useParams();
  const [t] = useTranslation();
  const [tabIndex, setTabIndex] = useState(TabIndex.Household);
  const queryClient = useQueryClient();
  const { showAlert } = useAlert();

  const { data: logs, isLoading: logsLoading }: { data: HouseholdLog[], isLoading: boolean } = useQuery(['logs', userId], async () => {
    const { data }: { data: HouseholdLogsResponse } = await HouseholdService.fetchHouseholdLogs(userId);
    return data.logs;
  }, { refetchOnWindowFocus: false });

  const { data: household, isLoading: householdLoading, isFetching: householdFetching }:
        { data: Household, isLoading: boolean, isFetching: boolean } = useQuery(['household', userId], async () => {
    const { data }: { data: Household } = await HouseholdService.fetchHousehold(userId);
    return data;
  }, { refetchOnWindowFocus: false });

  const executeAction = useMutation(
    async ({ action }: { action: Action }) => {
      return HouseholdService.performActionOnDevice(action.url);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['household', userId]);
        queryClient.invalidateQueries(['logs', userId]);
        showAlert({
          title: t('common.success'),
          message: t('household.householdActionExecuteSuccess'),
          severity: AlertSeverity.Success,
        });
      },
      onError: (e: Error) => {
        showAlert({
          title: t('household.householdActionExecuteError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
        console.error('Error executing action on device:', e);
      },
    }
  );

  const loading = householdLoading || householdFetching || logsLoading;

  const handleAction = useCallback(
    async (action: Action) => {
      await executeAction.mutateAsync({ action });
    },
    [executeAction]
  );

  return (
    <Box id="Household-container" className={classes.container}>
      <Panel title={
        <Box display="flex" alignItems="center" className={classes.title}>
          <Icon style={{ marginRight: 8 }}>home</Icon>
          <Typography>
            {
              household?.ipAddress
              ? `${t('household.householdIpAddress')}: ${household.ipAddress}`
              : (<Tooltip title={t('household.householdIpNotCapturedTooltip', { defaultValue: 'IP has not been captured for this customer.' })}>
                  <Typography component="span" color="textSecondary">{t('household.householdNoIpAddress')}</Typography>
                </Tooltip>)
            }
          </Typography>
        </Box>} id="Household-panel" variant="contained">
        {!(household && household.ipAddress) ? (
          <EmptyState variant="unavailable" message={t('household.householdDevicesNotAvailable')} />
        ) : (
          <>
            <Tabs
              id="Household-tabs"
              className={classes.tabs}
              data-value={tabIndex}
              value={tabIndex}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              onChange={(_event, index) => setTabIndex(index)}
            >
              <Tab
                id="Household-devicesTab"
                label={t('customer.customerDetailsDevices')}
                data-index={TabIndex.Household}
              />
              <Tab
                id="Household-logsTab"
                label={t('household.householdLogs')}
                data-index={TabIndex.Logs}
              />
            </Tabs>
            <Box id="Household-tabsContent" className={classes.content}>
              <TabPanel index={TabIndex.Household} currentIndex={tabIndex}>
                <HouseholdDevices
                  household={household}
                  handleAction={handleAction}
                  actionsDisabled={loading}
                />
              </TabPanel>
              <TabPanel index={TabIndex.Logs} currentIndex={tabIndex}>
                <HouseholdLogs data={logs} />
              </TabPanel>
            </Box>
          </>
        )}
      </Panel>
    </Box>
  );
}

export default HouseholdPage;
